const preloadedMedia = new Map();
const preconnectedOrigins = new Set();

const IMAGE_EXTENSIONS = new Set(['bmp', 'gif', 'jpg', 'jpeg', 'png', 'svg', 'webp', 'avif']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'aac', 'm4a']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v']);

function normalizeUrl(url) {
    if (!url) return '';
    try {
        if (typeof window !== 'undefined') {
            return new URL(url, window.location.href).href;
        }
    } catch {}
    return String(url);
}

function getExtension(url) {
    const clean = normalizeUrl(url).split('?')[0].split('#')[0];
    return (clean.split('.').pop() || '').toLowerCase();
}

function ensurePreconnect(url) {
    if (typeof document === 'undefined') return;
    try {
        const origin = new URL(normalizeUrl(url)).origin;
        if (!origin || preconnectedOrigins.has(origin)) return;

        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        link.crossOrigin = '';
        document.head.appendChild(link);

        preconnectedOrigins.add(origin);
    } catch {}
}

function getAsValue(extension) {
    if (IMAGE_EXTENSIONS.has(extension)) return 'image';
    if (AUDIO_EXTENSIONS.has(extension)) return 'audio';
    if (VIDEO_EXTENSIONS.has(extension)) return 'video';
    return 'fetch';
}

function preloadLink(url, asValue) {
    const normalizedUrl = normalizeUrl(url);
    if (!normalizedUrl || typeof document === 'undefined') {
        return Promise.resolve(normalizedUrl);
    }

    const cacheKey = `${asValue}:${normalizedUrl}`;
    if (preloadedMedia.has(cacheKey)) {
        return preloadedMedia.get(cacheKey);
    }

    ensurePreconnect(normalizedUrl);

    const promise = Promise.resolve().then(() => {
        // Only preload images → browser support is guaranteed
        if (asValue === 'image') {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = normalizedUrl;
            document.head.appendChild(link);
        }

        // Skip <link preload> for audio & video completely  
        // Otherwise errors WILL continue

        return normalizedUrl;
    });

    preloadedMedia.set(cacheKey, promise);
    return promise;
}

export function isImageUrl(url) {
    return IMAGE_EXTENSIONS.has(getExtension(url));
}

export function preloadImage(url, { fetchPriority = 'high' } = {}) {
    const normalizedUrl = normalizeUrl(url);
    if (!normalizedUrl || typeof Image === 'undefined') {
        return Promise.resolve(normalizedUrl);
    }

    if (preloadedMedia.has(normalizedUrl)) {
        return preloadedMedia.get(normalizedUrl);
    }

    ensurePreconnect(normalizedUrl);

    const promise = new Promise((resolve) => {
        const image = new Image();

        if ('fetchpriority' in image) {
            image.fetchpriority = fetchPriority;   
        }

        image.decoding = 'async';

        const finish = () => resolve(normalizedUrl);

        image.onload = () => {
            if (typeof image.decode === 'function') {
                image.decode().catch(() => {}).finally(finish);
                return;
            }
            finish();
        };

        image.onerror = finish;
        image.src = normalizedUrl;

        if (image.complete) finish();
    });

    preloadedMedia.set(normalizedUrl, promise);
    return promise;
}

export function preloadMediaUrl(url, options) {
    const ext = getExtension(url);

    if (IMAGE_EXTENSIONS.has(ext)) {
        return preloadImage(url, options);  // real preload
    }

    if (VIDEO_EXTENSIONS.has(ext)) {
        return Promise.resolve(url);       // skip preload
    }

    if (AUDIO_EXTENSIONS.has(ext)) {
        return Promise.resolve(url);       // skip preload
    }

    return Promise.resolve(url);
}

// export function preloadMediaUrl(url, options) {
//     const extension = getExtension(url);
//     const asValue = getAsValue(extension);

//     if (asValue === 'image') return preloadImage(url, options);

//     return preloadLink(url, asValue); 
// }

export function preloadMediaUrls(urls, options) {
    const uniqueUrls = [...new Set((Array.isArray(urls) ? urls : [urls])
        .filter(Boolean)
        .map(normalizeUrl)
    )];

    uniqueUrls.forEach((url) => preloadMediaUrl(url, options));
    return uniqueUrls;
}

export function collectMediaUrls(source, keys = ['MediaURL', 'Media_URL', 'AvatarMedia', 'SenderProfileImage']) {
    const entries = Array.isArray(source) ? source : (source ? [source] : []);
    return entries.flatMap((entry) =>
        keys.map((k) => entry[k]).filter(Boolean)
    );
}

export function extractLandingMediaUrls(mediaItems) {
    return collectMediaUrls(mediaItems, ['MediaURL', 'Media_URL']);
}

export function extractQuestionMediaUrls(q) {
    if (!q || typeof q !== 'object') return [];
    return [
        q.QuestionMediaID,
        q.MediaID_1,
        q.MediaID_2,
        q.MediaID_3,
        q.MediaID_4,
    ].filter(Boolean);
}

export function clearMediaPreloadCache() {
    preloadedMedia.clear();
    preconnectedOrigins.clear();
}